import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Shield, Plant, Storefront, ArrowRight } from "@phosphor-icons/react";

const DEMO_ACCOUNTS = [
  {
    id: "admin",
    email: "admin@worldfoods2u.app",
    token: "seed_token_admin_demo",
    role: "Admin",
    name: "WorldFoods2U Admin",
    desc: "Approve farmers/buyers, assign orders, view GMV & origin analytics, print branded labels.",
    path: "/admin",
    Icon: Shield,
    color: "from-gold/80 to-saffron/80",
  },
  {
    id: "buyer",
    email: "buyer.demo@worldfoods2u.app",
    token: "seed_token_buyer_demo_1",
    role: "Buyer",
    name: "Global Foods Inc",
    desc: "Browse 11 spices anonymously, add to cart, lock live FX rates, Stripe checkout.",
    path: "/shop",
    Icon: Storefront,
    color: "from-saffron to-saffron-deep",
  },
  {
    id: "farmer",
    email: "kerala.farm@worldfoods2u.app",
    token: "seed_token_farmer_kerala_1",
    role: "Farmer",
    name: "Kerala Spice Farm",
    desc: "Manage 6 product listings, view orders, update stock, complete KYC, print shipment labels.",
    path: "/farmer",
    Icon: Plant,
    color: "from-forest to-forest-dark",
  },
];

export default function Demo() {
  const nav = useNavigate();

  const loginAs = async (account) => {
    // Set token for Bearer-auth flow
    localStorage.setItem("spv_session_token", account.token);
    // Verify it works
    try {
      const r = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${account.token}` },
        withCredentials: true,
      });
      if (r.data) {
        // Hard reload to pick up user context cleanly
        window.location.href = account.path;
      }
    } catch (e) {
      alert("Demo token invalid — please run /api/seed to reseed the DB");
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-24 hero-gradient noise" data-testid="demo-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="section-label">Demo Mode · No signup required</p>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter mt-4 leading-[0.95]">
            Try WorldFoods2U in <em className="text-gradient-gold not-italic">one click.</em>
          </h1>
          <p className="text-muted mt-6 text-lg max-w-2xl">
            Pick a role below. You'll be instantly signed in as a pre-seeded demo account with realistic data —
            no Google login, no password, no wait.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {DEMO_ACCOUNTS.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * i }}
              className="glass rounded-3xl p-8 flex flex-col group hover:border-saffron/40 border border-white/10 transition-colors"
              data-testid={`demo-card-${a.id}`}
            >
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${a.color} flex items-center justify-center shadow-lg`}>
                <a.Icon size={24} weight="duotone" className="text-ink-950" />
              </div>
              <p className="section-label mt-6">Role</p>
              <h3 className="font-display text-3xl text-cream mt-1">{a.role}</h3>
              <p className="text-xs text-gold/90 uppercase tracking-[0.2em] mt-1">{a.name}</p>
              <p className="text-sm text-muted mt-4 leading-relaxed flex-1">{a.desc}</p>

              <div className="mt-6 space-y-1.5 text-xs">
                <div className="flex items-center gap-2 text-cream/70">
                  <span className="text-muted uppercase tracking-[0.15em] text-[10px] w-16">Email</span>
                  <span className="font-mono truncate">{a.email}</span>
                </div>
                <div className="flex items-center gap-2 text-cream/70">
                  <span className="text-muted uppercase tracking-[0.15em] text-[10px] w-16">Goes to</span>
                  <span className="font-mono">{a.path}</span>
                </div>
              </div>

              <button
                onClick={() => loginAs(a)}
                className={`mt-7 w-full justify-center btn-primary`}
                data-testid={`demo-login-${a.id}`}
              >
                Enter as {a.role} <ArrowRight size={14} weight="bold" />
              </button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 glass rounded-2xl p-6 text-center"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-muted">
            Demo accounts are seeded automatically. All three share the same catalog & order data so you can see
            the full farmer → buyer → admin workflow in real time.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
